import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { IconComponentV2 } from '../icon-component/icon.component';

export interface ProgressBarData {
  label: string;
  value: number; // percentage for the progress bar (0-100)
  color: string;
  displayValue?: string | number; // optional custom display value
  segments?: ProgressBarSegment[]; // optional segmented values for a single bar
  
  // Rich mode optional props
  description?: string;
  icon?: string;
  count?: number | string;
  growth?: string; // e.g. "+43.5%"
  growthTrend?: 'up' | 'down';
}

export interface ProgressBarSegment {
  value: number;
  color: string;
}

@Component({
  selector: 'app-data-progress-bar',
  standalone: true,
  imports: [CommonModule, MatProgressBarModule, IconComponentV2],
  templateUrl: './data-progress-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host ::ng-deep mat-progress-bar {
      height: 100% !important;
      border-radius: inherit !important;
    }
    :host ::ng-deep .mdc-linear-progress {
      height: 100% !important;
      border-radius: inherit !important;
    }
    :host ::ng-deep .mdc-linear-progress__bar {
      height: 100% !important;
      border-radius: inherit !important;
    }
    :host ::ng-deep .mdc-linear-progress__bar-inner {
      border-top-width: 0 !important;
      height: 100% !important;
      background-color: var(--mat-progress-bar-active-indicator-color, inherit) !important;
      border-radius: inherit !important;
    }
  `]
})
export class DataProgressBarComponent {
  @Input() data: ProgressBarData[] = [];
  @Input() heightClass: string = '!h-2';
  @Input() showBorders: boolean = false;
  @Input() showItemHeader: boolean = true;
  
  @Input() mode: 'default' | 'rich' = 'default';
  @Input() trackColor: string = 'bg-gray-100 dark:bg-gray-700';

  getNormalizedSegments(item: ProgressBarData): ProgressBarSegment[] {
    const segments = (item.segments ?? [])
      .map((segment) => ({
        value: Number(segment?.value ?? 0),
        color: segment?.color || item.color,
      }))
      .filter((segment) => segment.value > 0);

    if (!segments.length) {
      return [];
    }

    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    if (total <= 0) {
      return [];
    }

    return segments.map((segment) => ({
      ...segment,
      value: (segment.value / total) * 100,
    }));
  }
}
